<template>
  <div
    :class="{
      'work-status-snippet': true,
      'work-status-snippet--yellow-bg': !props.customCollabClientPayload.customerCollabInputs.markedCompleteDate && props.customCollabClientPayload.creatorCollabInputs.agreedToDate,
      'work-status-snippet--green-bg': props.customCollabClientPayload.customerCollabInputs.markedCompleteDate,
    }"
  >
    <div class="work-status-snippet__header">
      <h2 class="global-h2 work-status-snippet__title">Work Status:</h2>
      <CollabStatusStripe class="work-status-snippet__status-badge" :text="props.customCollabClientPayload.statusCustomerText" />
    </div>

    <div class="work-status-snippet__divider"></div>

    <!-- Creator notes snippet -->
    <div class="creator-notes-snippet work-status-snippet__creator-notes-snippet">
      <AvatarWithFallback class="creator-notes-snippet__avatar" :src="props.customCollabClientPayload.creator.creatorProfileImage" />

      <div class="creator-notes-snippet__name-and-note">
        <div class="global-h4 creator-notes-snippet__name">
          {{ props.customCollabClientPayload.creator.creatorName }}
        </div>

        <!-- Note itself -->
        <div v-if="props.customCollabClientPayload.creatorCollabInputs.creatorNotes" class="note-itself creator-notes-snippet__note-itself">
          <div class="note-itself__header">
            <IconEmbedded class="note-itself__icon" name="file-text_2" :size="19" color="rgba(0, 0, 0, 0.5)" />
            <div class="note-itself__title">Creator Notes</div>
          </div>
          <div class="note-itself__text">
            <ClampedText :visibleLines="7">
              {{ props.customCollabClientPayload.creatorCollabInputs.creatorNotes }}
            </ClampedText>
          </div>
        </div>
        <!-- / Note itself -->

        <!-- Note itself -->
        <!-- Note placeholder -->
        <div v-else class="note-itself creator-notes-snippet__note-itself">
          <template v-if="props.customCollabClientPayload.creatorCollabInputs.agreedToDate">
            <div class="note-itself__header">
              <IconEmbedded class="note-itself__icon" name="file-text_2" :size="19" color="rgba(0, 0, 0, 0.5)" />
              <div class="note-itself__title">Creator Notes</div>
            </div>
            <div class="note-itself__text"><i style="color: rgba(0, 0, 0, 0.3)">No notes have been added yet</i></div>
          </template>
          <template v-else>
            <div class="note-itself__header">
              <IconEmbedded class="note-itself__icon" name="file-text_2" :size="19" color="rgba(0, 0, 0, 0.5)" />
              <div class="note-itself__text" style="padding-left: 3px"><i>The creator's work notes will show here.</i></div>
            </div>
          </template>
        </div>
        <!-- / Note itself -->
      </div>
    </div>
    <!-- / Creator notes snippet -->

    <template v-if="props.customCollabClientPayload.creatorCollabInputs.deliverablesReadyDate">
      <div class="work-status-snippet__divider"></div>

      <!-- Deliverables snippet -->
      <div class="deliverables-snippet work-status-snippet__deliverables-snippet">
        <div class="global-h4 deliverables-snippet__title">Deliverables for Review</div>

        <div class="deliverables-snippet__text">The creator has uploaded deliverables for review:</div>
        <div class="deliverables-snippet__link-wrap" v-for="deliverableLink in props.customCollabClientPayload.creatorCollabInputs.deliverables" :key="deliverableLink">
          <div class="deliverables-snippet__link"><b>-</b> <span v-html="linkifyStr(deliverableLink, localLinkifyOptions)"></span></div>
          <!-- <IconEmbedded name="external-link_2" :size="15" color="rgba(0, 0, 0, 0.5)" style="position: relative; top: 2px" /> -->
        </div>
      </div>
      <!-- / Deliverables snippet -->
    </template>

    <template v-if="props.customCollabClientPayload.creatorCollabInputs.deliverablesReadyDate && !props.customCollabClientPayload.customerCollabInputs.markedCompleteDate">
      <div class="work-status-snippet__divider"></div>

      <!-- What's next snippet -->
      <div class="whats-next-snippet work-status-snippet__whats-next-snippet">
        <div class="global-h4 whats-next-snippet__title">What Next?</div>
        <div class="whats-next-snippet__text" style="margin-bottom: 18px">If you have questions or changes, message the creator.</div>
        <div class="whats-next-snippet__mark-as-paid-wrap">
          <div style="margin-bottom: 10px">
            <h6 class="global-h6" style="margin-bottom: 8px; font-weight: 600">Or if it looks good:</h6>
            <ol class="global-ol" style="gap: 7px; margin-bottom: 5px">
              <li>Transfer compensation to the creator</li>
              <li>Mark the collab complete</li>
            </ol>
          </div>
        </div>
        <SrpButton size="small" @click="emit('markComplete')">Mark Complete</SrpButton>
      </div>
      <!-- / What's next snippet -->
    </template>
  </div>
</template>

<script setup lang="ts">
import { inject } from "vue";
import linkifyStr from "linkify-string";
const globalRoot = inject("globalRoot") as any;

// Components
import CollabStatusStripe from "@components/CollabStatusStripe.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import AvatarWithFallback from "@components/AvatarWithFallback.vue";
import SrpButton from "@components/ui/SrpButton.vue";

// Types
import { CustomCollabClientPayload } from "@contracts/customCollabs";
import ClampedText from "@components/ui/ClampedText.vue";

const props = withDefaults(
  defineProps<{
    customCollabClientPayload: CustomCollabClientPayload | null;
  }>(),
  {
    customCollabClientPayload: null,
  }
);

const emit = defineEmits<{
  (event: "markComplete"): void;
}>();

// Create a local copy of linkifyOptions with truncate set to 100
const localLinkifyOptions = {
  ...globalRoot.linkifyOptions,
  truncate: 70,
};
</script>

<style scoped lang="scss">
@import "@/scss/screen-size-ranges.scss";
@import "@/scss/variables.scss";

// Note itself ================================================================
.note-itself {
  @include standardFont;
  padding: 11px 11px 13px;
  border-radius: 6px;
  position: relative;
  z-index: 0;
  background: rgba(255, 255, 255, 1);

  &__header {
    display: flex;
    align-items: center;
  }

  &__icon {
    margin-right: 3px;
  }

  &__title {
    font-weight: bold;
  }

  &__text {
    padding-left: 23px;
    white-space: pre-wrap;
  }
}

// Creator notes snippet ======================================================
.creator-notes-snippet {
  gap: 24px;
  display: flex;
  align-items: flex-start;

  &__avatar {
    width: 95px;
    min-width: 95px;
    position: relative;
    top: 2px;
  }

  &__name-and-note {
  }

  &__name {
    margin-bottom: 10px;
    font-weight: 500;
  }

  &__note-itself {
  }
}

// Deliverables snippet =======================================================
.deliverables-snippet {
  @include standardFont;
  padding: 15px 19px 18px;
  border-radius: 6px;
  background: rgba(0, 0, 0, 0.1);

  &__title {
    margin-bottom: 4px;
    font-weight: 500;
  }

  &__text {
    margin-bottom: 3px;
  }

  &__link-wrap {
  }

  &__link {
    margin-bottom: 3px;
  }
}

// What's next snippet ========================================================
.whats-next-snippet {
  @include standardFont;

  &__title {
    margin-bottom: 7px;
    font-weight: 500;
  }

  &__text {
    margin-bottom: 10px;
  }

  &__mark-as-paid-wrap {
    gap: 5px 6px;
    display: flex;
    align-items: center;
    //font-weight: bold;
  }
}

// Work status snippet ========================================================
.work-status-snippet {
  padding: 20px 25px;
  border-radius: 6px;
  background: rgba(240, 240, 240, 1);

  &--yellow-bg {
    background: rgba(240, 231, 184, 1);
  }
  &--green-bg {
    background: rgba(213, 236, 206, 1);
  }

  &__header {
    gap: 12px 15px;
    padding-bottom: 16px;
    display: flex;
    align-items: center;

    &:last-child {
      padding-bottom: 0;
    }
  }

  &__title {
  }

  &__status-badge {
  }

  &__divider {
    margin-bottom: 15px;
    border-bottom: 1px rgba(0, 0, 0, 0.15) solid;
  }

  &__creator-notes-snippet {
    margin-bottom: 25px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__deliverables-snippet {
    margin-bottom: 25px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__whats-next-snippet {
    margin-bottom: 25px;

    &:last-child {
      margin-bottom: 0;
    }
  }
}
</style>
